import { SlideMedia } from '../../../types/interfaces';

import { Image } from './Image';
import { MediaFactory } from './MediaFactory';
import { Slide } from './Slide';
import { Video } from './Video';

export class SlideArray {
  private static readonly ThumbnailsNumber = 9;

  private _slides: Slide[];

  private constructor(slides: Slide[]) {
    this._slides = [];
    for (const slide of slides) {
      if (!this._slides.find((s) => s.equals(slide))) {
        this._slides.push(slide);
      }
    }
  }

  get moreThanSingle(): boolean {
    return this._slides.length > 1;
  }

  get moreThanSlidesToShow(): boolean {
    return this._slides.length > this.slidesToShow;
  }

  get widthPercentage(): number {
    return this._slides.length <= SlideArray.ThumbnailsNumber
      ? (100 / SlideArray.ThumbnailsNumber) * this._slides.length
      : 100;
  }

  get slidesToShow(): number {
    return this._slides.length < SlideArray.ThumbnailsNumber
      ? this._slides.length
      : SlideArray.ThumbnailsNumber;
  }

  get all(): Slide[] {
    return this._slides;
  }

  get isEmpty(): boolean {
    return this._slides.length === 0;
  }

  get containsVideos(): boolean {
    for (const slide of this._slides) {
      if (slide.isVideo) {
        return true;
      }
    }
    return false;
  }

  get galleryImagesUrls(): string[] {
    return this._slides
      .filter((s) => s.isImage)
      .map((s) => s.media as Image)
      .map((i) => i.gallery);
  }

  get videos(): Video[] {
    return this._slides.filter((s) => s.isVideo).map((s) => s.media as Video);
  }

  get thumbnails(): string[] {
    return this._slides.map((slide) => slide.thumbnail);
  }

  static empty(): SlideArray {
    return new SlideArray([]);
  }

  getSlideNumber(slideNumber: number): Slide | undefined {
    return this.all[slideNumber - 1];
  }

  addFirst(slide: Slide): void {
    this._slides = [slide, ...this._slides];
    this.updatePositions();
  }

  remove(slide: Slide): void {
    const index = this._slides.findIndex((s) => s.equals(slide));
    if (index !== -1) {
      this._slides.splice(index, 1);
    }

    this.updatePositions();
  }

  copy(): SlideArray {
    const slideArrCopy = this._slides.map((slide) => slide.copy());
    const copy = new SlideArray(slideArrCopy);
    copy.updatePositions();
    return copy;
  }

  static fromSlides(...slides: Slide[][]): SlideArray {
    const newSlidesArray = new SlideArray(slides.flatMap((s) => s.map((slide) => slide.copy())));
    newSlidesArray.updatePositions();
    return newSlidesArray;
  }

  static fromBrandkitPreview(previewUrl: string, altText: string): SlideArray {
    return new SlideArray([new Slide(Image.fromPreviewUrl(previewUrl, altText))]);
  }

  static from(medias: SlideMedia[]): SlideArray {
    const mediaArray = new SlideArray(medias.map((media) => new Slide(MediaFactory.get(media))));

    mediaArray.updatePositions();

    return mediaArray;
  }

  private updatePositions(): void {
    const totalNumberOfMedias = this._slides.length;
    this._slides.forEach((media, index) => {
      media.position = index + 1;
      media.total = totalNumberOfMedias;
    });
  }
}
